import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, MapPin, Clock, Youtube, Instagram, Facebook, Send, Loader2, PartyPopper, ChevronDown, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { SITE, PROGRAM_OPTIONS, API } from "@/constants/site";
import { Reveal, Squiggle } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { parent_name: "", child_name: "", child_age: "", phone: "", email: "", program: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 h-auto text-base focus-visible:border-brand-sky focus-visible:ring-brand-sky/20 transition-colors";

export default function Admissions() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.program) {
      toast.error("Please select a program.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, email: form.email || null });
      toast.success("Thank you! We've received your enquiry and will call you soon.");
      setForm(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d;
      toast.error(msg || "Something went wrong. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="admissions-page" className="pb-24 md:pb-0 pt-36 md:pt-44">
      <header className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-4">Admissions & Contact</p>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tight leading-[1.02] max-w-3xl">
            Let's start your
            <span className="relative inline-block text-brand-deepcoral"> child's journey
              <Squiggle className="absolute -bottom-2 left-0 w-full text-brand-sun" />
            </span>
          </h1>
        </Reveal>

        <Reveal delay={0.15} className="mt-10">
          <div data-testid="admission-banner" className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-[2rem] bg-brand-coral px-7 py-6 text-white shadow-[0_20px_50px_rgba(251,113,133,0.35)]">
            <PartyPopper className="h-9 w-9 shrink-0 text-brand-sun" />
            <div>
              <p className="font-display text-xl md:text-2xl font-semibold">Admission Open — Limited Seats</p>
              <p className="text-white/85 text-sm">Session 2026–27 · Toddler to Class 8 · Contact us for fees & a campus visit.</p>
            </div>
          </div>
        </Reveal>
      </header>

      <section className="mx-auto max-w-7xl px-6 pt-16 grid gap-12 lg:grid-cols-5">
        {/* FORM */}
        <Reveal className="lg:col-span-3">
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-[2.5rem] border border-amber-200/50 bg-white p-7 md:p-10 shadow-[0_25px_60px_rgba(2,132,199,0.1)]"
          >
            <h2 className="font-display text-2xl md:text-3xl font-semibold">Admission Enquiry Form</h2>
            <p className="mt-1.5 text-sm text-slate-500">Fill this in — we'll call you back within working hours.</p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Field label="Parent Name *">
                <Input required data-testid="form-parent-name" value={form.parent_name} onChange={set("parent_name")} placeholder="e.g. Sunita Sharma" className={inputCls} />
              </Field>
              <Field label="Child Name *">
                <Input required data-testid="form-child-name" value={form.child_name} onChange={set("child_name")} placeholder="e.g. Aarav" className={inputCls} />
              </Field>
              <Field label="Child Age *">
                <Input required data-testid="form-child-age" value={form.child_age} onChange={set("child_age")} placeholder="e.g. 3.5 years" className={inputCls} />
              </Field>
              <Field label="Phone *">
                <Input required type="tel" data-testid="form-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" className={inputCls} />
              </Field>
              <Field label="Email">
                <Input type="email" data-testid="form-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </Field>
              <Field label="Program Interested *">
                <div className="relative">
                  <select
                    required
                    data-testid="form-program"
                    value={form.program}
                    onChange={set("program")}
                    className={`${inputCls} w-full appearance-none pr-10 ${form.program ? "text-ink" : "text-slate-400"}`}
                  >
                    <option value="" disabled>Select a program</option>
                    {PROGRAM_OPTIONS.map((p) => (
                      <option key={p} value={p} className="text-ink">{p}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Message">
                  <Textarea data-testid="form-message" value={form.message} onChange={set("message")} placeholder="Anything you'd like us to know — questions, preferred timing, etc." rows={4} className={inputCls} />
                </Field>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={sending}
              data-testid="form-submit-btn"
              whileTap={{ scale: 0.97 }}
              className="mt-8 inline-flex min-h-[52px] w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-coral px-10 font-bold text-white shadow-[0_10px_30px_rgba(251,113,133,0.4)] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending..." : "Submit Enquiry"}
            </motion.button>
          </form>
        </Reveal>

        {/* CONTACT CARD */}
        <Reveal delay={0.15} className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-1 gap-4">
            {[
              { href: `tel:${SITE.phone1}`, icon: Phone, label: "Call Us", val: `${SITE.phone1Display} · ${SITE.phone2Display}`, cls: "bg-brand-sky text-white", tid: "quick-call" },
              { href: SITE.whatsappLink, icon: MessageCircle, label: "WhatsApp", val: "Chat with the school office", cls: "bg-emerald-500 text-white", tid: "quick-whatsapp" },
              { href: `mailto:${SITE.email}`, icon: Mail, label: "Email", val: SITE.email, cls: "bg-brand-navy text-white", tid: "quick-email" },
            ].map((q) => (
              <a
                key={q.label}
                href={q.href}
                target={q.href.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                data-testid={q.tid}
                className={`group flex items-center gap-4 rounded-3xl ${q.cls} p-5 shadow-lg transition-transform duration-300 hover:-translate-y-1`}
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20">
                  <q.icon className="h-6 w-6" />
                </span>
                <span>
                  <span className="block font-display font-semibold text-lg">{q.label}</span>
                  <span className="block text-sm opacity-85">{q.val}</span>
                </span>
              </a>
            ))}
          </div>

          <a
            href="/files/parent-handbook.pdf"
            download
            data-testid="handbook-download"
            className="group flex items-center gap-4 rounded-3xl border-2 border-dashed border-brand-sun bg-brand-sun/10 p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-brand-sun/20"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-sun text-ink">
              <BookOpen className="h-6 w-6" />
            </span>
            <span>
              <span className="block font-display font-semibold text-lg">Parent Handbook</span>
              <span className="block text-sm text-slate-500">Timings, uniform, policies — download the PDF</span>
            </span>
          </a>

          <div className="rounded-[2rem] border border-amber-200/50 bg-white p-7 shadow-[0_15px_40px_rgba(2,132,199,0.08)]">
            <h3 className="font-display text-xl font-semibold flex items-center gap-2"><MapPin className="h-5 w-5 text-brand-deepcoral" /> Visit Us</h3>
            <a href={SITE.mapLink} target="_blank" rel="noreferrer" data-testid="contact-address" className="mt-2 block text-sm text-slate-600 hover:text-brand-deepsky transition-colors">
              {SITE.address}
            </a>
            <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Clock className="h-4 w-4 text-brand-deepsky" /> <span data-testid="contact-hours">{SITE.hours}</span>
            </p>
            <div className="mt-5 flex gap-3">
              {[
                [SITE.socials.youtube, Youtube, "youtube"],
                [SITE.socials.instagram, Instagram, "instagram"],
                [SITE.socials.facebook, Facebook, "facebook"],
                [SITE.socials.whatsapp, MessageCircle, "whatsapp"],
              ].map(([href, Icon, name]) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`contact-social-${name}`}
                  aria-label={name}
                  className="grid h-11 w-11 place-items-center rounded-full bg-brand-sky/15 text-brand-deepsky transition-all duration-300 hover:bg-brand-coral hover:text-white hover:scale-110"
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* MAP */}
      <section className="mx-auto max-w-7xl px-6 pt-16">
        <Reveal>
          <div data-testid="map-embed" className="overflow-hidden rounded-[2.5rem] border-8 border-white shadow-[0_25px_60px_rgba(2,132,199,0.15)]">
            <iframe
              title="Blooming Chaitanya Academy location"
              src={SITE.mapEmbed}
              className="h-[380px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
